import type { Lens } from '@hookform/lenses';
import { z } from 'zod/v4';
import BankIcon from '@interxfi/design-system/icons/react/icon-bank';
import type {
  IxcFundingV1BatchFundingControls,
  IxcFundingV1FundingAmountLimits,
  IxcFundingV1FundingControls,
  IxcFundingV1FundingControlsLimits,
  IxcFundingV1FundingControlsLimitsAch,
  IxcFundingV1FundingControlsLimitsSameDayAch,
  IxcFundingV1FundingControlsLimitsWire,
} from '@interxfi/types/generated/api';
import { parseNumber } from '@interxfi/utilities/number/parse';
import { AccordionFormGroup } from '../../../../../../components/card-group/form/accordion-form-group';
import { AppTextInputWithLens } from '../../../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../../../util/has-value';
import { getNumericStringValidator } from '../../../../../../util/validation';
import {
  getInitialFormValuesForProgramBatchFundingControls,
  ProgramControlsBatchFundingForm,
  programControlsBatchFundingFormDefaultValues,
  programControlsBatchFundingFormSchema,
} from './program-controls-batch-funding-form';
import {
  defaultProgramControlsFundingMethodWithLimitFormValues,
  ProgramControlsFundingMethodWithLimitsForm,
  programControlsFundingMethodWithLimitsFormSchema,
} from './program-controls-method-with-limits-form';

export const programControlsFundingFormSchema = z.object({
  ach: z.object({
    canDeposit: programControlsFundingMethodWithLimitsFormSchema,
    canWithdraw: programControlsFundingMethodWithLimitsFormSchema,
  }),
  sameDayAch: z.object({
    canDeposit: programControlsFundingMethodWithLimitsFormSchema,
    canWithdraw: programControlsFundingMethodWithLimitsFormSchema,
  }),
  wire: z.object({
    canWithdraw: programControlsFundingMethodWithLimitsFormSchema,
  }),
  maxSourcesPerAccount: getNumericStringValidator('Max Sources per Account', { required: '', gte: 0 }),
  batch: programControlsBatchFundingFormSchema,
});

export type ProgramControlsFundingFormValues = z.infer<typeof programControlsFundingFormSchema>;

export const programControlsFundingFormDefaultValues: ProgramControlsFundingFormValues = {
  ach: {
    canDeposit: defaultProgramControlsFundingMethodWithLimitFormValues,
    canWithdraw: defaultProgramControlsFundingMethodWithLimitFormValues,
  },
  sameDayAch: {
    canDeposit: defaultProgramControlsFundingMethodWithLimitFormValues,
    canWithdraw: defaultProgramControlsFundingMethodWithLimitFormValues,
  },
  wire: {
    canWithdraw: defaultProgramControlsFundingMethodWithLimitFormValues,
  },
  maxSourcesPerAccount: '',
  batch: programControlsBatchFundingFormDefaultValues,
};

export function getInitialFormValuesForProgramFundingControls(
  data: IxcFundingV1FundingControls | undefined,
  batch: IxcFundingV1BatchFundingControls | undefined,
): ProgramControlsFundingFormValues {
  if (!data) {
    return programControlsFundingFormDefaultValues;
  }

  return {
    ach: {
      canDeposit: {
        value: Boolean(data.settings?.ach?.canDeposit),
        limits: {
          minAmount: data.limits?.ach?.depositPerTransaction?.minAmount?.toString() || '',
          maxAmount: data.limits?.ach?.depositPerTransaction?.maxAmount?.toString() || '',
        },
      },
      canWithdraw: {
        value: Boolean(data.settings?.ach?.canWithdraw),
        limits: {
          minAmount: data.limits?.ach?.withdrawalPerTransaction?.minAmount?.toString() || '',
          maxAmount: data.limits?.ach?.withdrawalPerTransaction?.maxAmount?.toString() || '',
        },
      },
    },
    sameDayAch: {
      canDeposit: {
        value: Boolean(data.settings?.sameDayAch?.canDeposit),
        limits: {
          minAmount: data.limits?.sameDayAch?.depositPerTransaction?.minAmount?.toString() || '',
          maxAmount: data.limits?.sameDayAch?.depositPerTransaction?.maxAmount?.toString() || '',
        },
      },
      canWithdraw: {
        value: Boolean(data.settings?.sameDayAch?.canWithdraw),
        limits: {
          minAmount: data.limits?.sameDayAch?.withdrawalPerTransaction?.minAmount?.toString() || '',
          maxAmount: data.limits?.sameDayAch?.withdrawalPerTransaction?.maxAmount?.toString() || '',
        },
      },
    },
    wire: {
      canWithdraw: {
        value: Boolean(data.settings?.wire?.canWithdraw),
        limits: {
          minAmount: data.limits?.wire?.withdrawalPerTransaction?.minAmount?.toString() || '',
          maxAmount: data.limits?.wire?.withdrawalPerTransaction?.maxAmount?.toString() || '',
        },
      },
    },
    maxSourcesPerAccount: data.limits?.maxSourcesPerAccount?.toString() || '',
    batch: getInitialFormValuesForProgramBatchFundingControls(batch),
  };
}

function formValuesToWireLimits(values: ProgramControlsFundingFormValues) {
  // If no wire setting is set to truthy, don't bother with limits
  if (!Object.values(values.wire).some(Boolean)) {
    return undefined;
  }

  const withdrawalLimits = addDefinedValues<IxcFundingV1FundingAmountLimits>(
    {},
    values.wire.canWithdraw.value
      ? {
          minAmount: values.wire.canWithdraw.limits.minAmount ? parseNumber(values.wire.canWithdraw.limits.minAmount) : undefined,
          maxAmount: values.wire.canWithdraw.limits.maxAmount ? parseNumber(values.wire.canWithdraw.limits.maxAmount) : undefined,
        }
      : {},
  );

  const wireLimits = addDefinedValues<IxcFundingV1FundingControlsLimitsWire>(
    {},
    {
      withdrawalPerTransaction: Object.keys(withdrawalLimits).length ? withdrawalLimits : undefined,
    },
  );

  return Object.keys(wireLimits).length ? wireLimits : undefined;
}

function formValuesToAchLimits(values: ProgramControlsFundingFormValues) {
  // If no ACH setting is set to truthy, don't bother with limits
  if (!Object.values(values.ach).some(Boolean)) {
    return undefined;
  }

  const withdrawalLimits = addDefinedValues<IxcFundingV1FundingAmountLimits>(
    {},
    values.ach.canWithdraw.value
      ? {
          minAmount: values.ach.canWithdraw.limits.minAmount ? parseNumber(values.ach.canWithdraw.limits.minAmount) : undefined,
          maxAmount: values.ach.canWithdraw.limits.maxAmount ? parseNumber(values.ach.canWithdraw.limits.maxAmount) : undefined,
        }
      : {},
  );

  const depositLimits = addDefinedValues<IxcFundingV1FundingAmountLimits>(
    {},
    values.ach.canDeposit.value
      ? {
          minAmount: values.ach.canDeposit.limits.minAmount ? parseNumber(values.ach.canDeposit.limits.minAmount) : undefined,
          maxAmount: values.ach.canDeposit.limits.maxAmount ? parseNumber(values.ach.canDeposit.limits.maxAmount) : undefined,
        }
      : {},
  );

  const achLimits = addDefinedValues<IxcFundingV1FundingControlsLimitsAch>(
    {},
    {
      depositPerTransaction: Object.keys(depositLimits).length ? depositLimits : undefined,
      withdrawalPerTransaction: Object.keys(withdrawalLimits).length ? withdrawalLimits : undefined,
    },
  );

  return Object.keys(achLimits).length ? achLimits : undefined;
}

function formValuesToSameDayAchLimits(values: ProgramControlsFundingFormValues) {
  // If no Same-Day ACH setting is set to truthy, don't bother with limits
  if (!Object.values(values.sameDayAch).some(Boolean)) {
    return undefined;
  }

  const withdrawalLimits = addDefinedValues<IxcFundingV1FundingAmountLimits>(
    {},
    values.sameDayAch.canWithdraw.value
      ? {
          minAmount: values.sameDayAch.canWithdraw.limits.minAmount ? parseNumber(values.sameDayAch.canWithdraw.limits.minAmount) : undefined,
          maxAmount: values.sameDayAch.canWithdraw.limits.maxAmount ? parseNumber(values.sameDayAch.canWithdraw.limits.maxAmount) : undefined,
        }
      : {},
  );

  const depositLimits = addDefinedValues<IxcFundingV1FundingAmountLimits>(
    {},
    values.sameDayAch.canDeposit.value
      ? {
          minAmount: values.sameDayAch.canDeposit.limits.minAmount ? parseNumber(values.sameDayAch.canDeposit.limits.minAmount) : undefined,
          maxAmount: values.sameDayAch.canDeposit.limits.maxAmount ? parseNumber(values.sameDayAch.canDeposit.limits.maxAmount) : undefined,
        }
      : {},
  );

  const sameDayAchLimits = addDefinedValues<IxcFundingV1FundingControlsLimitsSameDayAch>(
    {},
    {
      depositPerTransaction: Object.keys(depositLimits).length ? depositLimits : undefined,
      withdrawalPerTransaction: Object.keys(withdrawalLimits).length ? withdrawalLimits : undefined,
    },
  );

  return Object.keys(sameDayAchLimits).length ? sameDayAchLimits : undefined;
}

export function formValuesToProgramFundingControls(values: ProgramControlsFundingFormValues): IxcFundingV1FundingControls {
  return {
    settings: {
      ach: {
        canDeposit: values.ach.canDeposit.value,
        canWithdraw: values.ach.canWithdraw.value,
      },
      sameDayAch: {
        canDeposit: values.sameDayAch.canDeposit.value,
        canWithdraw: values.sameDayAch.canWithdraw.value,
      },
      wire: {
        canWithdraw: values.wire.canWithdraw.value,
      },
    },
    limits: addDefinedValues<IxcFundingV1FundingControlsLimits>(
      {},
      {
        ach: formValuesToAchLimits(values),
        sameDayAch: formValuesToSameDayAchLimits(values),
        wire: formValuesToWireLimits(values),
        maxSourcesPerAccount: values.maxSourcesPerAccount ? Number(parseNumber(values.maxSourcesPerAccount)) : undefined,
      },
    ),
  };
}

interface ProgramFundingControlsFormProps {
  lens: Lens<ProgramControlsFundingFormValues>;
}

export function ProgramFundingControlsForm({ lens }: ProgramFundingControlsFormProps) {
  return (
    <AccordionFormGroup icon={<BankIcon aria-hidden />} title="Funding">
      <ProgramControlsFundingMethodWithLimitsForm lens={lens.focus('ach.canDeposit')} settingLabel="ACH Pull" />
      <ProgramControlsFundingMethodWithLimitsForm lens={lens.focus('ach.canWithdraw')} settingLabel="ACH Push" />

      <ProgramControlsFundingMethodWithLimitsForm lens={lens.focus('sameDayAch.canDeposit')} settingLabel="Same-Day ACH Pull" />
      <ProgramControlsFundingMethodWithLimitsForm lens={lens.focus('sameDayAch.canWithdraw')} settingLabel="Same-Day ACH Push" />

      <ProgramControlsFundingMethodWithLimitsForm lens={lens.focus('wire.canWithdraw')} settingLabel="Wire Push" />

      <AccordionFormGroup.Section>
        <AppTextInputWithLens label="Max Funding Sources" lens={lens.focus('maxSourcesPerAccount')} />
      </AccordionFormGroup.Section>

      <ProgramControlsBatchFundingForm lens={lens.focus('batch')} />
    </AccordionFormGroup>
  );
}
